import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { NewsForm } from "../components/NewsForm";
import { apiFetch } from "../lib/api";
import { formatDateLong } from "../lib/dates";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { can } from "@family/shared";
import type { NewsItem } from "@family/shared";

function displayCategory(cat: string): string {
  return cat
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function NewsDetailPage() {
  const { newsId } = useParams<{ newsId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;

  const isNew = newsId === "new";
  const [editing, setEditing] = useState(isNew);

  const query = useQuery<NewsItem>({
    queryKey: ["news-item", slug, newsId],
    queryFn: () => apiFetch<NewsItem>(`/families/${slug}/news/${newsId}`),
    enabled: Boolean(slug && newsId && !isNew),
  });

  const deleteMutation = useMutation<void, Error, void>({
    mutationFn: () =>
      apiFetch(`/families/${slug}/news/${newsId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["news", slug] });
      navigate("/news", { replace: true });
    },
  });

  function onSaved(saved: NewsItem) {
    setEditing(false);
    if (isNew) {
      navigate(`/news/${saved.id}`, { replace: true });
    } else {
      queryClient.setQueryData(["news-item", slug, newsId], saved);
    }
    void queryClient.invalidateQueries({ queryKey: ["news", slug] });
  }

  if (!primary || !slug) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }

  const item = query.data;
  const canWriteSelf = primary && can(primary.role, "post:create");
  const canManageAny = primary && can(primary.role, "post:delete:any");
  const isOwn = item?.author.id === meQuery.data?.user.id;
  const canEditThis = (isOwn && canWriteSelf) || canManageAny;

  if (isNew) {
    if (!canWriteSelf) {
      return (
        <>
          <AppHeader />
          <main className="page">
            <p className="text-error">Contributors only.</p>
          </main>
        </>
      );
    }
    return (
      <>
        <AppHeader />
        <main className="page stack-lg">
          <Link
            to="/news"
            className="text-muted"
            style={{ textDecoration: "none" }}
          >
            ← Back to news
          </Link>
          <h1>New news item</h1>
          <NewsForm
            familySlug={slug}
            onSaved={onSaved}
            onCancel={() => navigate("/news")}
          />
        </main>
      </>
    );
  }

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <Link
          to="/news"
          className="text-muted"
          style={{ textDecoration: "none" }}
        >
          ← Back to news
        </Link>

        {query.isLoading && <p className="text-muted">Loading…</p>}
        {query.error && (
          <p className="text-error">
            Error: {(query.error as Error).message}
          </p>
        )}

        {item && editing && (
          <NewsForm
            familySlug={slug}
            item={item}
            onSaved={onSaved}
            onCancel={() => setEditing(false)}
          />
        )}

        {item && !editing && (
          <article className="stack-lg">
            {item.coverUrl && (
              <div
                style={{
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "var(--c-bg-alt)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  maxHeight: "min(70vh, 720px)",
                }}
              >
                <img
                  src={item.coverUrl}
                  alt=""
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "min(70vh, 720px)",
                    objectFit: "contain",
                    display: "block",
                  }}
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                />
              </div>
            )}

            <div className="row-between">
              <div>
                <div
                  className="text-subtle"
                  style={{ fontSize: "0.85rem", marginBottom: 6 }}
                >
                  {formatDateLong(item.publishedAt)} ·{" "}
                  {displayCategory(item.category)} ·{" "}
                  {item.author.displayName ?? "—"}
                </div>
                <h1 style={{ margin: 0 }}>{item.title}</h1>
              </div>
              {canEditThis && (
                <div style={{ display: "flex", gap: "var(--s-2)" }}>
                  <button
                    className="btn-tertiary"
                    onClick={() => setEditing(true)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-tertiary"
                    style={{ color: "var(--c-danger)" }}
                    disabled={deleteMutation.isPending}
                    onClick={() => {
                      if (window.confirm(`Delete "${item.title}"?`)) {
                        deleteMutation.mutate();
                      }
                    }}
                  >
                    {deleteMutation.isPending ? "Deleting…" : "Delete"}
                  </button>
                </div>
              )}
            </div>

            {item.summary && (
              <p className="text-muted" style={{ fontSize: "1.05rem" }}>
                {item.summary}
              </p>
            )}

            {item.body && (
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.7,
                  fontSize: "1.05rem",
                }}
              >
                {item.body}
              </div>
            )}

            {deleteMutation.error && (
              <p className="text-error">{deleteMutation.error.message}</p>
            )}
          </article>
        )}
      </main>
    </>
  );
}
